import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { OPS } from "@/constants/testIds";

const C = OPS.checkin;
const DOW = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export default function CheckinScheduleDialog({ branchId, open, onOpenChange, onSaved }) {
    const { t } = useI18n();
    const [sched, setSched] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !branchId) return;
        setSched(null);
        (async () => {
            try {
                const r = await api.get(`/branches/${branchId}/checkin-schedule`);
                const norm = {};
                DOW.forEach((d) => {
                    const s = r.data.schedule[d] || {};
                    norm[d] = { open: s.open || "07:00", close: s.close || "23:30", closed: !!s.closed };
                });
                setSched(norm);
            } catch (e) {
                toast.error(e?.response?.data?.detail || t("error_generic"));
                onOpenChange(false);
            }
        })();
    }, [open, branchId, t, onOpenChange]);

    const setDay = (d, patch) => setSched((prev) => ({ ...prev, [d]: { ...prev[d], ...patch } }));

    const save = async () => {
        for (const d of DOW) {
            const day = sched[d];
            if (!day.closed && (!HHMM.test(day.open) || !HHMM.test(day.close))) {
                toast.error(t("checkin_time_invalid"));
                return;
            }
        }
        setSaving(true);
        try {
            await api.put(`/branches/${branchId}/checkin-schedule`, { schedule: sched });
            toast.success(t("checkin_schedule_saved"));
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={C.scheduleDialog} className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{t("checkin_schedule_settings")}</DialogTitle>
                    <DialogDescription>{t("checkin_schedule_desc")}</DialogDescription>
                </DialogHeader>

                {sched ? (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-thin pe-1">
                        <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--ink-faint)]">
                            <span>{t("date")}</span>
                            <span className="w-24 text-center">{t("checkin_open")}</span>
                            <span className="w-24 text-center">{t("checkin_close")}</span>
                            <span className="text-center">{t("checkin_closed_col")}</span>
                        </div>
                        {DOW.map((d) => {
                            const day = sched[d];
                            return (
                                <div key={d} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 rounded-lg surface-2 border border-[color:var(--border-soft)] px-2 py-2">
                                    <span className="text-sm font-medium text-[color:var(--ink)]">{t(`checkin_day_${d}`)}</span>
                                    <Input
                                        data-testid={C.scheduleOpen(d)}
                                        type="time"
                                        value={day.open}
                                        disabled={day.closed}
                                        onChange={(e) => setDay(d, { open: e.target.value })}
                                        className="w-24"
                                    />
                                    <Input
                                        data-testid={C.scheduleClose(d)}
                                        type="time"
                                        value={day.close}
                                        disabled={day.closed}
                                        onChange={(e) => setDay(d, { close: e.target.value })}
                                        className="w-24"
                                    />
                                    <div className="flex justify-center">
                                        <Switch
                                            data-testid={C.scheduleClosed(d)}
                                            checked={day.closed}
                                            onCheckedChange={(v) => setDay(d, { closed: v })}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <Skeleton className="h-64 w-full" />
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
                    <Button data-testid={C.scheduleSave} onClick={save} disabled={saving || !sched}>
                        {saving ? t("saving") : t("save")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
